import React, { useState } from 'react';
import CodeOfConductModal from './CodeOfConductModal';
import { EntryRequirementNotice, EntryRequirementCheckbox } from './EntryRequirementNotice';

export interface CasualTicketFormData {
  name: string;
  email: string;
  company?: string;
  code_of_conduct_accepted: boolean;
  privacy_policy_accepted: boolean;
  entry_requirement_accepted?: boolean;
  newsletter_opt_in: boolean;
}

interface CasualTicketRequestFormProps {
  formData: CasualTicketFormData;
  onChange: (data: CasualTicketFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitButtonText?: string;
  isSubmitting?: boolean;
  showPrivacyPolicy?: boolean;
  autoFocus?: boolean;
  entryRequirement?: string;
}

export const CasualTicketRequestForm: React.FC<CasualTicketRequestFormProps> = ({
  formData,
  onChange,
  onSubmit,
  submitButtonText = 'Register',
  isSubmitting = false,
  showPrivacyPolicy = true,
  autoFocus = false,
  entryRequirement,
}) => {
  const [showCodeOfConduct, setShowCodeOfConduct] = useState(false);

  const handleFieldChange = (field: keyof CasualTicketFormData, value: any) => {
    onChange({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {entryRequirement && <EntryRequirementNotice text={entryRequirement} />}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-blue-800 text-sm">
          <strong>Quick Registration</strong> - This is a casual event. Just a few quick details and you're all set!
        </p>
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          required
          value={formData.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="John Doe"
          autoFocus={autoFocus}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          required
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
          Company/Organization <span className="text-gray-500">(optional)</span>
        </label>
        <input
          type="text"
          id="company"
          value={formData.company || ''}
          onChange={(e) => handleFieldChange('company', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Acme Inc."
        />
      </div>

      <div className="space-y-3 pt-2">
        {entryRequirement && (
          <EntryRequirementCheckbox
            id="entry_requirement"
            checked={!!formData.entry_requirement_accepted}
            onChange={(v) => handleFieldChange('entry_requirement_accepted', v)}
          />
        )}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="code_of_conduct"
            required
            checked={formData.code_of_conduct_accepted}
            onChange={(e) => handleFieldChange('code_of_conduct_accepted', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
          />
          <label htmlFor="code_of_conduct" className="ml-2 text-sm text-gray-700">
            I agree to the{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowCodeOfConduct(true);
              }}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Code of Conduct
            </button>
            {' '}<span className="text-red-500">*</span>
          </label>
        </div>

        {showPrivacyPolicy && (
          <div className="flex items-start">
            <input
              type="checkbox"
              id="privacy_policy"
              required
              checked={formData.privacy_policy_accepted}
              onChange={(e) => handleFieldChange('privacy_policy_accepted', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="privacy_policy" className="ml-2 text-sm text-gray-700">
              I agree to the{' '}
              <a
                href="https://star.london/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                Privacy Policy
              </a>
              {' '}<span className="text-red-500">*</span>
            </label>
          </div>
        )}

        <div className="flex items-start">
          <input
            type="checkbox"
            id="newsletter"
            checked={formData.newsletter_opt_in}
            onChange={(e) => handleFieldChange('newsletter_opt_in', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
          />
          <label htmlFor="newsletter" className="ml-2 text-sm text-gray-700">
            Keep me updated about future events (optional)
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {isSubmitting ? 'Processing...' : submitButtonText}
      </button>

      {showCodeOfConduct && (
        <CodeOfConductModal
          isOpen={showCodeOfConduct}
          onClose={() => setShowCodeOfConduct(false)}
        />
      )}
    </form>
  );
};

export default CasualTicketRequestForm;